import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LicenseProvider, LicenseWarningBanner } from "@/components/LicenseManager";
import { SettingsProvider } from "@/context/SettingsContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import TodaysEvents from "@/pages/TodaysEvents";
import Events from "@/pages/Events";
import Matches from "@/pages/Matches";
import EventLeaderboards from "@/pages/EventLeaderboards";
import Shooters from "@/pages/Shooters";
import ScoreEntry from "@/pages/ScoreEntry";
import Leaderboard from "@/pages/Leaderboard";
import Payments from "@/pages/Payments";
import MobileScores from "@/pages/MobileScores";
import Sponsors from "@/pages/Sponsors";
import ShootOffs from "@/pages/ShootOffs";
import ShootOffBoard from "@/pages/ShootOffBoard";
import Users from "@/pages/Users";
import Announcements from "@/pages/Announcements";
import Reports from "@/pages/Reports";
import SearchResults from "@/pages/SearchResults";
import MyResults from "@/pages/MyResults";
import ScoreHistory from "@/pages/ScoreHistory";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import MatchResults from "@/pages/MatchResults";
import AccountSettings from "@/pages/AccountSettings";
import System from "@/pages/System";
import EventRegistrations from "@/pages/EventRegistrations";

// Protected Route wrapper
function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Public route - redirect to dashboard if logged in
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes - no auth required */}
      <Route path="/scores" element={<MobileScores />} />
      <Route path="/scores/:matchId" element={<MobileScores />} />
      
      {/* Login */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="today" element={<TodaysEvents />} />
        <Route path="events" element={<Events />} />
        <Route path="matches/:eventId" element={<Matches />} />
        <Route path="event-leaderboards/:eventId" element={<EventLeaderboards />} />
        <Route
          path="event-registrations/:eventId"
          element={
            <ProtectedRoute adminOnly>
              <EventRegistrations />
            </ProtectedRoute>
          }
        />
        <Route path="shooters" element={<Shooters />} />
        <Route
          path="score-entry"
          element={
            <ProtectedRoute adminOnly>
              <ScoreEntry />
            </ProtectedRoute>
          }
        />
        <Route
          path="score-entry/:matchId"
          element={
            <ProtectedRoute adminOnly>
              <ScoreEntry />
            </ProtectedRoute>
          }
        />
        <Route path="leaderboard" element={<Leaderboard />} />
        <Route path="leaderboard/:matchId" element={<Leaderboard />} />
        <Route path="shootoffs" element={<ShootOffs />} />
        <Route path="shootoff-board/:eventId" element={<ShootOffBoard />} />
        <Route path="announcements" element={<Announcements />} />
        <Route path="search" element={<SearchResults />} />
        <Route path="my-results" element={<MyResults />} />
        <Route path="profile" element={<Profile />} />
        <Route path="account-settings" element={<AccountSettings />} />
        <Route
          path="score-history"
          element={
            <ProtectedRoute adminOnly>
              <ScoreHistory />
            </ProtectedRoute>
          }
        />
        
        {/* Admin only routes */}
        <Route
          path="payments"
          element={
            <ProtectedRoute adminOnly>
              <Payments />
            </ProtectedRoute>
          }
        />
        <Route
          path="sponsors"
          element={
            <ProtectedRoute adminOnly>
              <Sponsors />
            </ProtectedRoute>
          }
        />
        <Route
          path="users"
          element={
            <ProtectedRoute adminOnly>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute adminOnly>
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute adminOnly>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="match-results"
          element={
            <ProtectedRoute adminOnly>
              <MatchResults />
            </ProtectedRoute>
          }
        />
        <Route
          path="system"
          element={
            <ProtectedRoute adminOnly>
              <System />
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <LicenseProvider>
              <LicenseWarningBanner />
              <AppRoutes />
            </LicenseProvider>
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
